import apiClient from './meta-client';

/** Dinheiro em centavos; percentuais já em escala 0-100, como nas outras telas. */
export type PeriodoGeral = 'hoje' | 'ontem' | '7d' | '30d' | 'mes';

export interface GestorGeral {
    user_id: number;
    owner_name?: string | null;
    paginas_ativas: number;
    receita: number;
    receita_flow: number;
    receita_broadcast: number;
    receita_anterior: number;
    receita_variacao: number | null;
    captados: number;
    captados_anterior: number;
    captados_variacao: number | null;
    perdidos_551: number;
    saldo: number;
    alcancados: number;
    cliques: number;
    produtivas: number;
    so_captacao: number;
    dormentes: number;
    funis: number;
    funis_ativos: number;
    rotinas_no_ar: number;
    broadcasts: number;
}

export interface CanalGeral {
    enviadas: number | null;
    entregues: number | null;
    taxa_entrega_pct: number | null;
    cliques: number | null;
    ctr_pct: number | null;
    receita: number | null;
    rpc: number | null;
}

export interface PaisLeads {
    codigo: string;
    captados: number;
    perdidos_551: number;
    saldo: number;
    receita?: number | null;
}

export interface RecorteGeral {
    nome: string;
    chave?: string;
    enviados?: number | null;
    entregues?: number | null;
    taxa_entrega_pct?: number | null;
    cliques?: number | null;
    ctr_pct?: number | null;
    receita?: number | null;
}

export interface FunilGeral {
    id: number;
    nome: string;
    cliques?: number | null;
    ctr_pct?: number | null;
    conclusao_pct?: number | null;
    receita?: number | null;
}

export interface BroadcastGeral {
    id: number;
    nome: string;
    hora?: string | null;
    status?: string;
    taxa_entrega_pct?: number | null;
    ctr_pct?: number | null;
    receita?: number | null;
}

/** Bloco que falhou no servidor volta como `{erro}` e a tela mostra só ele como indisponível. */
export type Bloco<T> = T | { erro: string };
export const falhou = (b: unknown): b is { erro: string } =>
    !!b && typeof b === 'object' && !Array.isArray(b) && 'erro' in (b as object);

export interface DashGeral {
    periodo: { chave: PeriodoGeral; inicio: string; fim: string; dias: number; parcial: boolean };
    gerado_em: string;
    leads: Bloco<{
        receita: {
            total: number; flow: number; broadcast: number;
            flow_participacao: number; broadcast_participacao: number;
            total_periodo_anterior: number; variacao_pct: number | null;
        };
        base: {
            captados: number; captados_variacao_pct: number | null;
            perdidos_551: number; perdidos_sobre_captados_pct: number | null;
            saldo: number; alcancados_periodo: number; ltv_por_lead_alcancado: number;
            cobertura_pct?: number | null;
        };
        controle_paginas: { cadastradas: number; pausadas: number };
        controle_grupos: Record<string, number>;
        paises: PaisLeads[];
        base_alcancavel: {
            janela_dias: number;
            ponte: { inicio: number; entraram: number; perdidos_551: number; outros: number; fim: number } | null;
            serie: { data: string; alcancaveis: number; captados: number; perdidos_551: number }[];
        };
    }>;
    gestores: Bloco<GestorGeral[]>;
    envio: {
        canais: Bloco<{ flow: CanalGeral; broadcast: CanalGeral } | null>;
        cliques_por_dia: Bloco<{ rot: string; flow: number | null; broadcast: number | null }[]>;
    };
    rankings: {
        funis: Bloco<FunilGeral[]>;
        broadcasts: Bloco<BroadcastGeral[]>;
        paises: Bloco<RecorteGeral[]>;
        dominios: Bloco<RecorteGeral[]>;
    };
}

export const getDashGeral = async (periodo: PeriodoGeral, userId?: number): Promise<DashGeral> => {
    const { data } = await apiClient.get<DashGeral>('/meta/admin/geral', {
        params: { periodo, user_id: userId },
    });
    return data;
};
